import React, { useState } from 'react';
import { MessageResponse } from 'stream-chat';

type Props = {
  result: MessageResponse;
  query: string;
};

const renderHighlightedHTML = (text: string, query: string) => {
  if (!query) {
    return text;
  }
  // Create a regular expression for case-insensitive search of the query in the HTML
  // 'gi' means global and case-insensitive
  // will match both uppercase and lowercase characters
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  // Replace matches in HTML with a span tag that has a highlight class
  const highlightedHtml = text.replace(
    regex,
    (highlight) => `<span>${highlight}</span>`,
  );
  // Return the highlighted HTML
  return highlightedHtml;
};

const ELLIPSES = '...';

const MessageTextRenderer: React.FC<Props> = ({ result, query }) => {
  const { html = '', id } = result;
  const [messageExpand, setMessageExpand] = useState<string>();

  const lines = html.split('\n');
  // Return null if there is no HTML string
  if (!html) {
    return null;
  }

  // Check if the message has less than or equal to 4 lines
  // If greater than 4, we need to render the truncated message in a manner whereby only first two and last two lines are shown.
  // In between them, we need to show an ellipsis and on click of the ellipsis, we need to expand the message to show all the lines.
  // Else, we can simply render the message as it is.

  if (lines.length <= 4) {
    return (
      <div>
        {lines.map((line, index) => (
          <p
            key={index}
            dangerouslySetInnerHTML={{
              __html: renderHighlightedHTML(line, query),
            }}
          />
        ))}
      </div>
    );
  }

  // If the message has more than 4 lines, we need to render the message in a truncated manner
  return (
    <div>
      {lines.map((line, index, arr) => {
        // Now that lines are more than four, it renders the first two lines, then an ellipsis, then the last two lines
        if (index === 0 || index === arr.length - 2 || id === messageExpand) {
          return (
            <p
              key={index}
              dangerouslySetInnerHTML={{
                __html: renderHighlightedHTML(line, query),
              }}
            />
          );
        } else if (index === 1) {
          return (
            <p
              key={index}
              onClick={(event) => {
                event.stopPropagation();
                setMessageExpand(id);
              }}
              style={{ marginBottom: 2 }}
            >
              {ELLIPSES}
            </p>
          );
        }
        return null;
      })}
    </div>
  );
};

export default MessageTextRenderer;
